import React, { useState } from "react";
import { ArrowRight, CheckCircle2, LockKeyhole, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../services/api";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [demoResetUrl, setDemoResetUrl] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDemoResetUrl(data.demoResetUrl || "");
      setSubmitted(true);
    } catch (err) {
      setError(t("passwordRecovery.sendError"));
    }
  };

  return (
    <main className="page-shell flex min-h-[calc(100vh-80px)] items-center justify-center">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-gold-100 bg-white shadow-xl lg:grid-cols-[0.85fr_1.15fr]">
        <aside className="relative hidden overflow-hidden bg-forest p-8 text-white lg:block">
          <div aria-hidden="true" className="absolute -right-12 -top-12 h-64 w-64 rounded-full border-[24px] border-gold-400/35" />
          <div aria-hidden="true" className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full border-[32px] border-white/10" />
          <div className="relative">
            <p className="eyebrow text-gold-400">{t("passwordRecovery.securityFirst")}</p>
            <h1 className="mt-5 font-serif text-4xl font-bold leading-tight">{t("passwordRecovery.title")}</h1>
            <p className="mt-5 text-sm leading-7 text-green-100">
              {t("passwordRecovery.intro")}
            </p>
            <div className="mt-10 space-y-4 text-sm text-green-100">
              <p className="flex items-center gap-3"><CheckCircle2 className="text-gold-400" size={18} /> {t("passwordRecovery.noRestart")}</p>
              <p className="flex items-center gap-3"><CheckCircle2 className="text-gold-400" size={18} /> {t("passwordRecovery.fastRecovery")}</p>
              <p className="flex items-center gap-3"><CheckCircle2 className="text-gold-400" size={18} /> {t("passwordRecovery.protectedAccess")}</p>
            </div>
          </div>
        </aside>

        <section className="p-6 sm:p-10">
          <div className="mb-8 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold-50 text-gold-600">
              <LockKeyhole size={22} />
            </span>
            <div>
              <p className="eyebrow">{t("passwordRecovery.eyebrow")}</p>
              <h2 className="font-serif text-3xl font-bold text-forest">{t("passwordRecovery.heading")}</h2>
            </div>
          </div>

          {error && <p className="mb-5 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p>}

          {submitted ? (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
              <div className="flex items-center gap-3 text-emerald-700">
                <CheckCircle2 size={20} />
                <p className="font-semibold">{t("passwordRecovery.resetSent")}</p>
              </div>
              <p className="mt-3 text-sm leading-6 text-emerald-800">
                {t("passwordRecovery.sentText", { email })}
              </p>
              {demoResetUrl && <a href={demoResetUrl} className="btn-primary mt-4 w-full">{t("passwordRecovery.openDemoLink")}</a>}
              <Link to="/login" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:underline">
                {t("passwordRecovery.backToLogin")} <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="label">{t("passwordRecovery.email")}</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    className="input pl-11"
                    type="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t("common.emailPlaceholder")}
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary w-full">
                {t("passwordRecovery.sendLink")} <ArrowRight size={16} />
              </button>

              <p className="text-center text-sm text-gray-500">
                {t("passwordRecovery.remembered")} <Link to="/login" className="font-semibold text-gold-700 hover:underline">{t("passwordRecovery.signIn")}</Link>
              </p>
            </form>
          )}
        </section>
      </div>
    </main>
  );
};

export default ForgotPassword;
